import { createIntl, createIntlCache } from 'react-intl';

export type LoadMomentLocale = (locale: string) => Promise<void> | void;

export type ModuleMessages = Record<string, string>;

// ── Show translation keys mode ──────────────────────────────────────────

const SHOW_KEYS_STORAGE_KEY = 'xfm_show_translation_keys';

export const isShowTranslationKeysEnabled = (): boolean => {
  try {
    return localStorage.getItem(SHOW_KEYS_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const setShowTranslationKeys = (enabled: boolean): void => {
  try {
    localStorage.setItem(SHOW_KEYS_STORAGE_KEY, String(enabled));
  } catch {
    // ignore
  }
};

const isAcceptableBcp47Locale = (tag: string): boolean => {
  try {
    new Intl.Locale(tag);
    return true;
  } catch {
    return false;
  }
};

export const toBcp47Locale = (rawLocale: string): string => {
  const normalized = rawLocale.replace('-', '_').toLowerCase();
  const [language, region] = normalized.split('_');
  const languageTag =
    language && isAcceptableBcp47Locale(language) ? language : 'en';
  const fullTag = region
    ? `${languageTag}-${region.toUpperCase()}`
    : languageTag;

  return isAcceptableBcp47Locale(fullTag) ? fullTag : languageTag;
};

const resolveLocale = (localeOverride?: string) => {
  const match = window.location.pathname.match(/^\/([^/]+)\/([^/]+)/);
  const rawLocale = localeOverride ?? match?.[2];

  if (rawLocale) {
    const locale = rawLocale.replace('-', '_').toLowerCase();
    const localeCode = toBcp47Locale(locale);
    return { locale, localeCode };
  }

  return { locale: 'en_us', localeCode: 'en-US' };
};

/**
 * Resolve the primary and fallback base URLs for translation JSON files.
 *
 * - VITE_TRANSLATIONS_CDN_URL: env-specific CDN path (e.g. .../translations/qa)
 * - VITE_TRANSLATIONS_CDN_URL_FALLBACK: shared CDN path (e.g. .../translations)
 * - Local dev without either: '/translations' (Vite dev server middleware)
 *
 * VITE_TRANSLATIONS_BASE_URL can override everything (for testing).
 */
const getTranslationsUrls = (): { primary: string; fallback: string } => {
  try {
    // Full override for both
    const overrideUrl = import.meta.env.VITE_TRANSLATIONS_BASE_URL;
    if (overrideUrl) {
      const url = overrideUrl.replace(/\/$/, '');
      return { primary: url, fallback: url };
    }
  } catch {
    // continue
  }

  const primary = (
    import.meta.env.VITE_TRANSLATIONS_CDN_URL ?? '/translations'
  ).replace(/\/$/, '');

  const fallback = (
    import.meta.env.VITE_TRANSLATIONS_CDN_URL_FALLBACK ?? primary
  ).replace(/\/$/, '');

  return { primary, fallback };
};

// ── Local translations (VITE_TRANSLATIONS_LOCAL=true) ──────────────────

const isLocalTranslationsEnabled = (): boolean => {
  try {
    return import.meta.env.VITE_TRANSLATIONS_LOCAL === 'true';
  } catch {
    return false;
  }
};

// ── Remote translations (default) ──────────────────────────────────────

/**
 * Load translations via fetch().
 *
 * Resolution chain:
 *   1. {primary}/{locale}.json   — env-specific CDN
 *   2. {fallback}/{locale}.json  — shared CDN fallback
 *   3. {primary}/en.json         — English env-specific
 *   4. {fallback}/en.json        — English shared fallback
 *   5. {} (empty)
 */
const loadRemoteTranslationMessages = async (
  locale: string,
): Promise<ModuleMessages> => {
  const { primary, fallback } = getTranslationsUrls();

  // Helper: try fetching, return null on failure
  const tryFetch = async (url: string): Promise<ModuleMessages | null> => {
    try {
      const res = await fetch(url);
      if (res.ok) return (await res.json()) as ModuleMessages;
    } catch {
      // fall through
    }
    return null;
  };

  // 1. Primary (env-specific)
  const result = await tryFetch(`${primary}/${locale}.json`);
  if (result) return result;

  // 2. Fallback (shared) — only if different from primary
  if (fallback !== primary) {
    const fallbackResult = await tryFetch(`${fallback}/${locale}.json`);
    if (fallbackResult) return fallbackResult;
  }

  console.warn(
    `No translations found for locale: ${locale}, trying English fallback...`,
  );

  // 3. English primary
  if (locale !== 'en') {
    const enResult = await tryFetch(`${primary}/en.json`);
    if (enResult) return enResult;

    // 4. English fallback
    if (fallback !== primary) {
      const enFallback = await tryFetch(`${fallback}/en.json`);
      if (enFallback) return enFallback;
    }
  }

  return {};
};

// ── Unified loader ─────────────────────────────────────────────────────

const loadTranslationMessages = async (
  locale: string,
): Promise<ModuleMessages> => {
  // When VITE_TRANSLATIONS_LOCAL=true, load from the Vite dev server
  // middleware that serves translations/merged/ on /translations/.
  if (isLocalTranslationsEnabled()) {
    const localUrl = `/translations/${locale}.json`;
    try {
      const res = await fetch(localUrl);
      if (res.ok) {
        console.info(
          `[i18n] Loaded local translations for "${locale}" from translations/merged/`,
        );
        return (await res.json()) as ModuleMessages;
      }
    } catch {
      // fall through
    }

    // Fallback to English locally
    if (locale !== 'en') {
      try {
        const enRes = await fetch('/translations/en.json');
        if (enRes.ok) {
          console.warn(
            `[i18n] Local translations not found for "${locale}", using local English fallback`,
          );
          return (await enRes.json()) as ModuleMessages;
        }
      } catch {
        // fall through
      }
    }

    console.warn(
      `[i18n] VITE_TRANSLATIONS_LOCAL=true but no local file found for "${locale}", falling back to remote`,
    );
  }

  return loadRemoteTranslationMessages(locale);
};

// ── createModuleIntl ────────────────────────────────────────────────────

export interface CreateModuleIntlOptions {
  loadMomentLocale?: LoadMomentLocale;
  locale?: string;
}

export const createModuleIntl = async (args?: CreateModuleIntlOptions) => {
  const { locale, localeCode } = resolveLocale(args?.locale);

  let messages: ModuleMessages = await loadTranslationMessages(locale);

  // When "show translation keys" mode is active, replace every
  // translation value with its key so the UI renders the i18n IDs.
  if (isShowTranslationKeysEnabled()) {
    const keyMessages: ModuleMessages = {};
    for (const key of Object.keys(messages)) {
      keyMessages[key] = key;
    }
    messages = keyMessages;
  }

  await args?.loadMomentLocale?.(locale);

  return createIntl(
    {
      locale: localeCode,
      messages,
    },
    createIntlCache(),
  );
};
